import PublicLayout from "@/components/PublicLayout";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Compass, Target, HeartHandshake } from "lucide-react";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1762504351153-58a41cd486dd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBtZWV0aW5nfGVufDB8fHx8MTc4Mzc0NjgwNHww&ixlib=rb-4.1.0&q=85";

export default function About() {
  return (
    <PublicLayout>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-10">
        <div className="text-xs uppercase tracking-widest text-blue-700">About Us</div>
        <h1 className="h-display mt-3 text-5xl md:text-6xl text-slate-900 max-w-4xl">Chartered advisors with a small-firm heart, big-firm rigour.</h1>
        <p className="mt-6 max-w-2xl text-slate-600 leading-relaxed">
          KK Associates is a Rajkot-based accounting and business consulting firm. For over 15 years we have partnered with proprietors, MSMEs, LLPs, private limited companies, exporters and NRIs — providing the clarity and structure needed to grow with confidence.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
        <div className="lg:col-span-6 rounded-3xl overflow-hidden">
          <img src={ABOUT_IMG} alt="Team collaboration" className="w-full h-full object-cover min-h-[380px]" />
        </div>
        <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { i: Compass, t: "Vision", d: "To be Gujarat's most trusted independent CA practice by combining ethics, expertise, and technology." },
            { i: Target, t: "Mission", d: "Make compliance calm and predictable so our clients can focus on building great businesses." },
            { i: HeartHandshake, t: "Core Values", d: "Integrity, transparency, ownership, and long-term relationships over short-term wins." },
            { i: Award, t: "Why Trust Us", d: "Senior review on every file, encrypted documents, and SLA-based turnarounds." },
          ].map((x, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 p-6 bg-white hover:border-blue-300 hover:shadow-md transition-shadow duration-300">
              <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center mb-4"><x.i size={18} /></div>
              <div className="text-lg font-medium text-slate-900">{x.t}</div>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{x.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <h2 className="h-display text-4xl md:text-5xl text-slate-900 max-w-3xl">Meet the team</h2>
          <p className="mt-4 text-slate-600 max-w-2xl">A close-knit team of Chartered Accountants, Company Secretaries and finance specialists.</p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: "CA Kunal K.", role: "Founder & Managing Partner", bio: "15+ years across direct tax, GST and Virtual CFO engagements." },
              { name: "CA Krishna P.", role: "Partner — Assurance & Audit", bio: "Statutory and internal audit specialist for MSMEs and manufacturers." },
              { name: "CS Meera J.", role: "Company Secretary", bio: "ROC compliance, secretarial audit and startup structuring." },
            ].map((m, i) => (
              <div key={i} className="rounded-2xl bg-white border border-slate-200 p-6">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-700 to-slate-900 text-white flex items-center justify-center h-display text-xl">
                  {m.name.split(" ").slice(-1)[0][0]}
                </div>
                <div className="mt-4 text-lg font-medium text-slate-900">{m.name}</div>
                <div className="text-sm text-blue-700">{m.role}</div>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{m.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="rounded-3xl bg-slate-950 text-white p-10 md:p-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h3 className="h-display text-3xl md:text-4xl">Have a project in mind?</h3>
            <p className="mt-2 text-slate-300">Talk to a senior partner — we usually reply within a business day.</p>
          </div>
          <Link to="/contact" data-testid="about-cta" className="btn-hover rounded-full bg-white text-slate-900 px-6 py-3 text-sm font-medium hover:bg-blue-300 inline-flex items-center gap-2">
            Book Consultation <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
